import React, { useEffect, useState } from 'react'
import DonutLargeIcon from '@mui/icons-material/DonutLarge'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import NormalText from 'common/Typography/NormalText'
import useIdentity from 'hooks/useIdentity'

import {REFUND_ROLE_PERCENT} from './constant'
import { RefundFormV3 } from './RefundFormV3'

export function RefundDataCheckV2({
  allocationId,
  catalogName,
  faberToken,
  refundDialogOpen,
  setIsDataUsageZero,
  customerEmail,
  isRefundLoading,
  handleRefundLoading,
  customerId,
  transactionType,
  mutateRefundOtpack,
  paymentId,
  amount,
  currency,
  handleDialogClose
}: DataCheckProps) {
  //DATA CHECK FOR OTPACKS
  const [refundAmount, setRefundAmount] = useState('0')
  const [maxRefundAmount, setMaxRefundAmount] = useState('0')
  const [totalOtpackDataUsed, setTotalOtpackDataUsed] = useState(0)
  const [totalpackDataUsedPercentage, setTotalpackDataUsedPercentage] = useState(0)
  const [totalOtpackData, setTotalOtpackData] = useState(0)
  const [selectedValue, setSelectedRefundValue] = useState('refund_partial')
  const { identity } = useIdentity()
  const role = identity?.role || '';
  const maximumRefundPercentage = REFUND_ROLE_PERCENT[role] ?? 0

  //GET ALL ACTIVE OTPACK USER PLANS API
  const {
    data: userPlan,
    isLoading: isLoadingUserPlan,
    refetch: refetchUserPlan
  } = useQuery(['get-user-plan-by-payment-id', customerId], async () => {
    const userPlan = await OneTimePackApi.getUserPlanByPaymentId(
      customerId,
      (paymentId = `al_${allocationId}`),
      faberToken
    )
    return userPlan
  })

  const handleChangeRefundType = (event) => {
    setSelectedRefundValue(event.target.value)
    if (event.target.value === 'refund_full') {
      setRefundAmount(maxRefundAmount)
    }
  }

  useEffect(() => {
    if (userPlan?.data) {
      let validityInDays: number | null = null

      // totalData with -1 for unlimited packs
      if (userPlan.data.data.totalData === -1) {
        validityInDays = userPlan.data.data.validity
      }

      const totalDataUsed =
        userPlan.data.data.dataUsage > 0 ? userPlan.data.data.dataUsage : 0
      const totalData =
        validityInDays === null
          ? userPlan.data.data.totalData
          : validityInDays * 3

      const dataUsedPercentage =
        ((totalDataUsed || 0) / (totalData || 1)) * 100 || 0

      setTotalOtpackDataUsed(totalDataUsed)
      setTotalOtpackData(totalData)
      setTotalpackDataUsedPercentage(dataUsedPercentage)
      setSelectedRefundValue('refund_full')

      const refundAmount = amount * (1 - totalDataUsed / totalData)

      const fixedAmount = isNaN(refundAmount)
        ? String(amount)
        : refundAmount.toFixed(2)

      setRefundAmount(fixedAmount)
      setMaxRefundAmount(fixedAmount)

      setIsDataUsageZero(totalDataUsed === 0)
    } else {
      setRefundAmount(String(amount))
      setMaxRefundAmount(String(amount))

      setTotalOtpackDataUsed(0)
      setTotalOtpackData(0)
      setTotalpackDataUsedPercentage(0)

      setIsDataUsageZero(true)
    }
  }, [userPlan, amount])

  useEffect(() => {
    refetchUserPlan()
  }, [refundDialogOpen])

  return (
    <>
      {isLoadingUserPlan ? (
        <CircularProgress />
      ) : (
        <>
          {totalpackDataUsedPercentage > maximumRefundPercentage ? (
            <Alert severity='error' sx={{ mx: 2, mt: '1rem' }}>
              {`Customer has used more than ${maximumRefundPercentage}% of data of their active
              one-time-packs, no refunds should be given`}
            </Alert>
          ) : null}

          <Accordion
            defaultExpanded
            sx={{
              mt: 1,
              '&.MuiPaper-root': {
                boxShadow: 'none'
              },
              '&::before': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='panel1a-content'
              id='panel1a-header'
              sx={{
                '.MuiAccordionSummary-content': {
                  m: 0,
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column'
                },
                m: 0,
                py: 0,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'start'
              }}
            >
              <Box
                sx={{
                  maxHeight: '20px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  m: 0,
                  p: 0
                }}
              >
                <Typography
                  sx={{
                    display: 'flex',
                    width: '18px',
                    height: '18px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '50%',
                    backgroundColor: '#000868',
                    color: '#FFFFFF',
                    fontSize: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  1
                </Typography>

                <NormalText
                  isBold
                  sx={{ color: 'primary.dark', fontSize: '1em' }}
                >
                  Request Form
                </NormalText>
              </Box>
              <Typography
                sx={{
                  p: 0,
                  m: 0,
                  color: '#000868',
                  fontSize: '12px',
                  fontWeight: 400,
                  textAlign: 'start',
                  width: '100%',
                  display: 'block',
                  alignSelf: 'flex-start'
                }}
              >
                {catalogName}
              </Typography>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                py: 0,
                m: 0
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column'
                }}
              >
                <FormControl component='fieldset'>
                    <RadioGroup
                      row
                      value={selectedValue}
                      onChange={handleChangeRefundType}
                      name='radio-buttons-group'
                    >
                      <FormControlLabel
                        value='refund_full'
                        control={<Radio />}
                        label='Full Refund'
                        sx={{
                          '& .MuiTypography-root': {
                            color:
                              selectedValue === 'refund_full'
                                ? '#000868'
                                : 'initial',
                            fontFamily: 'SF Pro Display, sans-serif',
                            fontSize: '12px',
                            fontWeight:
                              selectedValue === 'refund_full' ? 700 : 400,
                            letterSpacing: '0.28px'
                          }
                        }}
                      />
                      <FormControlLabel
                        value='refund_partial'
                        control={<Radio />}
                        label='Partial Refund'
                        sx={{
                          '& .MuiTypography-root': {
                            color:
                              selectedValue === 'refund_partial'
                                ? '#000868'
                                : 'initial',
                            fontFamily: 'SF Pro Display, sans-serif',
                            fontSize: '12px',
                            fontWeight:
                              selectedValue === 'refund_partial' ? 700 : 400,
                            letterSpacing: '0.28px'
                          }
                        }}
                      />
                    </RadioGroup>
                </FormControl>

                <Box
                  sx={{
                    border: '1px solid #B0B0B0',
                    borderRadius: '12px',
                    width: '80%',
                    height: 'auto',
                    padding: '12px',
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'left',
                    gap: 2
                  }}
                >
                  <DonutLargeIcon
                    sx={{
                      color: '#000868'
                    }}
                  />
                  <Typography
                    sx={{
                      m: 0,
                      color: '#000868',
                      fontSize: '14px',
                      fontWeight: 700,
                      textAlign: 'justify'
                    }}
                  >
                    Customer&apos;s Data Usage:
                  </Typography>

                  <Box
                    sx={{
                      p: 0,
                      margin: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'end',
                      justifyContent: 'start'
                    }}
                  >
                    {totalOtpackData > 0 ? (
                      <Box
                        sx={{
                          p: 0,
                          m: 0,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'start',
                          gap: 0.5,
                          width: '100%',
                          maxHeight: '10px'
                        }}
                      >
                        <Typography
                          sx={{
                            p: 0,
                            margin: 0,
                            color: '#66BB6A',
                            fontSize: '16px',
                            fontWeight: 700,
                            textAlign: 'end'
                          }}
                        >
                          {totalOtpackDataUsed} GB
                        </Typography>
                        <Typography
                          sx={{
                            p: 0,
                            margin: 0,
                            color: '#000868',
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'end'
                          }}
                        >
                          out of {totalOtpackData} GB
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 0,
                          m: 0,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'start',
                          gap: 0.5,
                          width: '100%',
                          maxHeight: '10px'
                        }}
                      >
                        <NormalText
                          theme='warning'
                          sx={{
                            fontStyle: 'italic',
                            textAlign: 'center',
                            fontSize: 12
                          }}
                        >
                          {`User Plan not found!`}
                        </NormalText>
                      </Box>
                    )}

                    <Typography
                      sx={{
                        p: 0,
                        m: 0,
                        color: '#000868',
                        fontSize: '11px',
                        fontWeight: 400,
                        textAlign: 'start',
                        width: '100%',
                        display: 'block',
                        alignSelf: 'flex-start'
                      }}
                    >
                      {catalogName}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {totalpackDataUsedPercentage > maximumRefundPercentage ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'start',
                      alignItems: 'start',
                      flexDirection: 'column',
                      mt: 4
                    }}
                  >
                    <Typography
                      sx={{
                        p: 0,
                        margin: 0,
                        color: 'primary.dark',
                        fontSize: '16px',
                        fontWeight: 700,
                        textAlign: 'end'
                      }}
                    >
                      This pack cannot be refunded
                    </Typography>
                    <Typography
                        sx={{
                        mt: 2,
                        p: 0,
                        margin: 0,
                        color: '#9E9E9E',
                        fontFamily: 'SF Pro Display, sans-serif',
                        fontSize: '12px',
                        fontWeight: 400,
                        letterSpacing: '0.05px',
                        textAlign: 'start'
                      }}
                    >
                      {`Refunds are only available for packs with less than ${maximumRefundPercentage}% data usage. This pack has exceeded that limit.`}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'end', px: 2, mt: 3 }}>
                    <LoadingButton
                      variant='contained'
                      sx={{
                        '&.Mui-disabled': {
                          border: 'none'
                        }
                      }}  
                      onClick={handleDialogClose}
                    >
                      Close
                    </LoadingButton>
                  </Box>
                </>
              ) : (
                <>
                  <RefundFormV3
                    refundType={selectedValue}
                    maxRefundAmount={maxRefundAmount}
                    amount={refundAmount}
                    setRefundAmount={setRefundAmount}
                    currency={currency}
                    mutateRefund={mutateRefundOtpack}
                    transactionType={transactionType}
                    key='refund-form'
                    totalOtpackDataUsed={totalOtpackDataUsed}
                    totalpackDataUsedPercentage={totalpackDataUsedPercentage}
                    maximumRefundPercentage={maximumRefundPercentage}
                    customerEmail={customerEmail}
                    isRefundLoading={isRefundLoading}
                    handleRefundLoading={handleRefundLoading}
                    customerId={customerId}
                    allocationId={allocationId}
                    paymentId={paymentId}
                  />
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </>
  )
}

interface DataCheckProps {
  allocationId?: string
  catalogName?: string
  faberToken: string
  iccid: string
  refundDialogOpen: boolean
  setIsDataUsageZero: (value: boolean) => void
  customerEmail: string
  isRefundLoading: boolean
  amount: number
  currency?: string
  handleRefundLoading: () => void
  customerId: string
  transactionType: string
  mutateRefundOtpack?: ({
    faberToken,
    invoiceID,
    refundReason,
    userId,
    ticketId,
    refundAmount,
    currency,
    terminatePlan
  }) => void
  paymentId: string
  handleDialogClose: () => void
}
